import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { getOrderToken, getLastOrderNumber } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { OrderSummary } from "../components/OrderSummary";

/**
 * epoint ödənişdən sonra müştərini bu səhifələrə yönləndirir:
 *   /payment-success  (və səhv yazılışlı /payment-succsess)
 *   /payment-error
 *
 * Sifariş nömrəsi ünvan parametrindən gəlir; gəlmirsə brauzerdə
 * saxlanılan son sifariş nömrəsindən götürülür.
 *
 * VACİB: ekranda göstərilən nəticə ünvan sətrindən yox, serverdəki
 * real `payment_status` dəyərindən asılıdır — link əl ilə dəyişdirilə bilər.
 */
const PaymentStatus = ({ variant }) => {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const orderNumber =
    params.get("order") ||
    params.get("order_id") ||
    params.get("order_number") ||
    getLastOrderNumber();

  useEffect(() => {
    if (!orderNumber) {
      setLoading(false);
      return;
    }
    const token = getOrderToken(orderNumber);
    api
      .get(`/orders/${orderNumber}`, { params: token ? { token } : undefined })
      .then(({ data }) => setOrder(data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  // Serverdəki real status əsasdır.
  // Sifariş oxuna bilmirsə "ödəniş alındı" DEMİRİK — təsdiqlənməmiş ödənişi
  // uğurlu göstərmək müştərini yanıldar. Belə halda gözləmə görünüşü verilir.
  const status = loading
    ? null
    : order
    ? { paid: "ok", failed: "fail", refunded: "fail" }[order.payment_status] || "pending"
    : variant === "fail"
    ? "fail"
    : "pending";

  const view = {
    ok: {
      Icon: CheckCircle2,
      title: t("pay_ok_title"),
      sub: t("pay_ok_sub"),
      tone: "text-brand-ink",
    },
    fail: {
      Icon: XCircle,
      title: t("pay_fail_title"),
      sub: t("pay_fail_sub"),
      tone: "text-red-700",
    },
    pending: {
      Icon: Loader2,
      title: t("pay_pending_title"),
      sub: t("pay_pending_sub"),
      tone: "text-brand-sub",
    },
  }[status] || {
    Icon: Loader2,
    title: t("loading"),
    sub: "",
    tone: "text-brand-sub",
  };

  const { Icon, title, sub, tone } = view;

  return (
    <div
      className="max-w-2xl mx-auto px-6 py-24 text-center"
      data-testid={`payment-${status}`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Icon
          size={56}
          strokeWidth={1}
          className={`mx-auto ${tone} ${status === "pending" ? "animate-spin" : ""}`}
        />
      </motion.div>

      <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-8">
        {title}
      </h1>
      <p className="mt-6 text-brand-sub font-light">{sub}</p>

      {orderNumber && (
        <>
          <p className="overline mt-10">{t("order_number")}</p>
          <p className="font-mono text-2xl text-brand-ink mt-1" data-testid="payment-order-number">
            {orderNumber}
          </p>
        </>
      )}

      {loading && <p className="mt-8 text-brand-muted text-sm">{t("loading")}</p>}

      {order && (
        <div className="mt-12 border border-brand-line p-8 text-left">
          <OrderSummary order={order} />
        </div>
      )}

      <div className="mt-12 flex flex-wrap gap-4 justify-center">
        {status === "fail" && (
          <Link
            to="/checkout"
            className="bg-brand-ink text-white px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-brand-oxid transition-colors"
            data-testid="payment-retry"
          >
            {t("pay_retry")}
          </Link>
        )}
        <Link
          to="/"
          className={`px-8 py-4 text-sm tracking-[0.12em] uppercase transition-colors border ${
            status === "fail"
              ? "border-brand-line hover:border-brand-ink"
              : "bg-brand-ink text-white border-brand-ink hover:bg-brand-oxid"
          }`}
          data-testid="payment-back-home"
        >
          {t("back_home")}
        </Link>
      </div>

      <p className="mt-8 text-xs text-brand-muted">{t("pay_help")}</p>
    </div>
  );
};

export default PaymentStatus;
